import { FunctionComponent, useEffect } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";

import { Modal } from "@/components/Modals/Modal";
import { InputField } from "@/components/InputField/InputField";
import { SelectField, SelectFieldValue } from "@/components/SelectField/SelectField";

import { ContactChannelData } from "./types";
import { deriveNameParts } from "./nameParts";
import { ContactInfo, ContactInfoSchema } from "./schema";

interface ContactEditModalProps {
	data: ContactChannelData;
	isOpen: boolean;
	isLoading: boolean;
	onClose: () => void;
	onSubmit: (values: ContactInfo) => Promise<unknown>;
}

const toDefaults = (data: ContactChannelData): ContactInfo => {
	const { firstName, middleName, lastName } = deriveNameParts(data);

	return {
		id: data.id,
		firstName,
		middleName,
		lastName,
		gender: data.gender || "",
		email: data.email || "",
		phone: data.phone || "",
		linkedinUrl: data.linkedinUrl || "",
		bookingLink: data.bookingLink || "",
	};
};

const EDITABLE_FIELDS = [
	"firstName",
	"middleName",
	"lastName",
	"gender",
	"email",
	"phone",
	"linkedinUrl",
	"bookingLink",
] as const;

type EditableField = (typeof EDITABLE_FIELDS)[number];

const NAME_FIELDS = ["firstName", "middleName", "lastName"] as const;

/**
 * The fields the user actually touched, plus the id. Submitting the untouched
 * ones too would send empty strings for the fields that are empty on the record
 * — and since the backend recomputes the full name from the name parts, a
 * gender-only edit used to blank the lead's name (PREC-3759).
 *
 * The name parts are the one exception: editing one of them makes the backend
 * rebuild the full name from all three, so they always travel together — the
 * saved name then matches exactly what the modal showed.
 */
const toChangedValues = (
	values: ContactInfo,
	dirtyFields: Partial<Record<EditableField, boolean>>
): ContactInfo => {
	const changed: ContactInfo = { id: values.id };
	const nameEdited = NAME_FIELDS.some((field) => dirtyFields[field]);

	for (const field of EDITABLE_FIELDS) {
		const isNamePart = (NAME_FIELDS as readonly string[]).includes(field);

		if (dirtyFields[field] || (isNamePart && nameEdited)) {
			changed[field] = values[field];
		}
	}

	return changed;
};

export const ContactEditModal: FunctionComponent<ContactEditModalProps> = ({
	data,
	isOpen,
	isLoading,
	onClose,
	onSubmit,
}) => {
	const { t } = useTranslation();

	const {
		setValue,
		watch,
		handleSubmit,
		reset,
		formState: { errors, dirtyFields },
	} = useForm<ContactInfo>({
		defaultValues: toDefaults(data),
		resolver: zodResolver(ContactInfoSchema),
	});

	const values = watch();

	useEffect(() => {
		reset(toDefaults(data));
	}, [data, reset]);

	// `shouldDirty` is what makes a field show up in `dirtyFields` — without it
	// every save would look like "nothing changed" and submit no field at all.
	const setField = (field: EditableField, value: string) =>
		setValue(field, value, { shouldDirty: true });

	const submit = async (formValues: ContactInfo) => {
		const changed = toChangedValues(formValues, dirtyFields);

		if (Object.keys(changed).length === 1) {
			onClose();

			return;
		}

		try {
			await onSubmit(changed);
			onClose();
		} catch (err) {
			console.error(err);
		}
	};

	const genderOptions = [
		{ title: t("contactChannels.male", "Male"), value: "male" },
		{ title: t("contactChannels.female", "Female"), value: "female" },
	];

	return (
		<Modal
			handleClose={onClose}
			handleSave={handleSubmit(submit)}
			isLoading={isLoading}
			isOpened={isOpen}
			size="sm"
			title={t("contactChannels.editTitle", "Edit Contact Info")}
		>
			<form onSubmit={handleSubmit(submit)}>
				<div className="flex flex-col gap-3">
					<SelectField
						hideEmptyValue={false}
						label={t("contactChannels.gender", "Gender")}
						name="gender"
						options={genderOptions}
						placeholder={t("contactChannels.genderPlaceholder", "Not set")}
						selectedOption={values.gender || null}
						handleSelect={(value: SelectFieldValue) => setField("gender", (value as string) ?? "")}
					/>

					<div className="flex flex-row gap-2">
						<InputField
							error={!!errors.firstName?.message}
							handleChange={(e) => setField("firstName", e.target.value)}
							label={t("contactChannels.firstName", "First name")}
							name="firstName"
							value={values.firstName || ""}
						/>
						<InputField
							error={!!errors.middleName?.message}
							handleChange={(e) => setField("middleName", e.target.value)}
							label={t("contactChannels.middleName", "Middle name")}
							name="middleName"
							value={values.middleName || ""}
						/>
					</div>

					<InputField
						error={!!errors.lastName?.message}
						handleChange={(e) => setField("lastName", e.target.value)}
						label={t("contactChannels.lastName", "Last name")}
						name="lastName"
						value={values.lastName || ""}
					/>

					<InputField
						error={!!errors.linkedinUrl?.message}
						handleChange={(e) => setField("linkedinUrl", e.target.value)}
						label={t("contactChannels.linkedin", "LinkedIn URL")}
						name="linkedinUrl"
						value={values.linkedinUrl || ""}
					/>

					<InputField
						error={!!errors.email?.message}
						handleChange={(e) => setField("email", e.target.value)}
						label={t("contactChannels.email", "Email")}
						name="email"
						value={values.email || ""}
					/>

					<InputField
						error={!!errors.phone?.message}
						handleChange={(e) => setField("phone", e.target.value)}
						label={t("contactChannels.phone", "Phone")}
						name="phone"
						value={values.phone || ""}
					/>

					<InputField
						error={!!errors.bookingLink?.message}
						handleChange={(e) => setField("bookingLink", e.target.value)}
						label={t("contactChannels.bookingLink", "Booking link")}
						name="bookingLink"
						value={values.bookingLink || ""}
					/>
				</div>
			</form>
		</Modal>
	);
};
